import { convertMmToM } from '../unit';
import { cloneSelectionItemRefData } from './cloneBlendFeatureDefinitionData';

const MIN_EXTRUDE_VALUE = 1e-6;
const DEFAULT_EXTRUDE_LENGTH = convertMmToM(10);

/**
 * 复制终止目标的稳定引用，隔离响应式代理及预览期间的后续修改。
 * @param target 终止目标。
 * @returns 纯数据目标；未选择时返回 null。
 */
export function cloneExtrudeEndTarget(target: ExtrudeEndTarget | null | undefined): ExtrudeEndTarget | null {
  if (!target) return null;
  if (target.kind === 'body') {
    return { kind: 'body', bodyObjectId: target.bodyObjectId, owningFeatureId: target.owningFeatureId };
  }
  if (target.kind === 'sketch-point') {
    return { kind: target.kind, sketchId: target.sketchId, pointId: target.pointId, runtimePoint: target.runtimePoint ? [...target.runtimePoint] : undefined };
  }
  if (target.kind === 'datum-plane') {
    return { kind: target.kind, planeId: target.planeId, defaultPlaneKind: target.defaultPlaneKind,
      runtimePlane: target.runtimePlane ? { origin: [...target.runtimePlane.origin], normal: [...target.runtimePlane.normal] } : undefined };
  }
  return { kind: 'model-topology', selection: cloneSelectionItemRefData(target.selection) };
}

/**
 * 归一化后的拔模定义。
 */
export interface NormalizedExtrudeDraftDefinition extends ExtrudeDraftDefinition {
  /** 拔模角度（弧度）。 */
  angleRad: number;
}

/**
 * 归一化后的单方向定义。
 */
export interface NormalizedExtrudeDirectionDefinition extends ExtrudeDirectionDefinition {
  /** 归一化后的拔模定义。 */
  draft: NormalizedExtrudeDraftDefinition;
}

/**
 * 归一化后的薄壁特征定义。
 */
export interface NormalizedExtrudeThinFeatureDefinition extends ExtrudeThinFeatureDefinition {}

/**
 * 归一化后的拉伸特征定义。
 */
export interface NormalizedExtrudeFeatureDefinition {
  /** 方向 1 定义。 */
  direction1: NormalizedExtrudeDirectionDefinition;
  /** 方向 2 定义。 */
  direction2: NormalizedExtrudeDirectionDefinition;
  /** 薄壁特征定义。 */
  thinFeature: NormalizedExtrudeThinFeatureDefinition;
  /** 兼容旧执行链的总深度。 */
  legacyDepth: number;
  /** 兼容旧执行链的主方向。 */
  legacyDirection: 'normal' | 'reverse';
  /** 兼容旧执行链的起始偏移。 */
  legacyStartOffset: number;
}

/**
 * 创建默认拔模定义。
 *
 * @returns 默认拔模定义。
 */
export function createDefaultExtrudeDraftDefinition(): ExtrudeDraftDefinition {
  return {
    enabled: false,
    angleDeg: 1,
    outward: false,
  };
}

/**
 * 创建默认方向定义。
 *
 * @param overrides 覆盖参数。
 * @returns 默认方向定义。
 */
export function createDefaultExtrudeDirectionDefinition(
  overrides: Partial<ExtrudeDirectionDefinition> = {},
): ExtrudeDirectionDefinition {
  return {
    enabled: true,
    endCondition: 'blind',
    depth: DEFAULT_EXTRUDE_LENGTH,
    reverse: false,
    target: null,
    offset: DEFAULT_EXTRUDE_LENGTH,
    reverseOffset: false,
    translateSurface: false,
    ...overrides,
    draft: {
      ...createDefaultExtrudeDraftDefinition(),
      ...(overrides.draft ?? {}),
    },
  };
}

/**
 * 创建默认薄壁特征定义。
 *
 * @returns 默认薄壁特征定义。
 */
export function createDefaultExtrudeThinFeatureDefinition(): ExtrudeThinFeatureDefinition {
  return {
    enabled: false,
    type: 'one-direction',
    reverse: false,
    thickness: DEFAULT_EXTRUDE_LENGTH,
    thickness1: DEFAULT_EXTRUDE_LENGTH,
    thickness2: DEFAULT_EXTRUDE_LENGTH,
    capEnds: false,
    capThickness: DEFAULT_EXTRUDE_LENGTH,
  };
}

/**
 * 归一化数值输入。
 *
 * @param value 输入值。
 * @param fallback 回退值。
 * @returns 有效的非负数值。
 */
function resolvePositiveNumber(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(MIN_EXTRUDE_VALUE, Math.abs(value));
}

/**
 * 归一化布尔值输入。
 *
 * @param value 输入值。
 * @param fallback 回退值。
 * @returns 布尔结果。
 */
function resolveBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value !== 'boolean') {
    return fallback;
  }
  return value;
}

/**
 * 从旧版 flat 参数推导方向 1。
 *
 * @param params 原始拉伸参数。
 * @returns 方向 1 定义。
 */
function resolveDirection1FromLegacyParams(params: ExtrudeParams): ExtrudeDirectionDefinition {
  const direction1Depth = typeof params.startOffset === 'number' && params.startOffset < 0
    ? Math.max(MIN_EXTRUDE_VALUE, Math.abs(params.depth + params.startOffset))
    : resolvePositiveNumber(params.depth, DEFAULT_EXTRUDE_LENGTH);

  return createDefaultExtrudeDirectionDefinition({
    depth: direction1Depth,
    reverse: params.direction === 'reverse',
  });
}

/**
 * 从旧版 flat 参数推导方向 2。
 *
 * @param params 原始拉伸参数。
 * @returns 方向 2 定义。
 */
function resolveDirection2FromLegacyParams(params: ExtrudeParams): ExtrudeDirectionDefinition {
  const legacyBackwardDepth = typeof params.startOffset === 'number' && params.startOffset < 0
    ? Math.abs(params.startOffset)
    : 0;
  return createDefaultExtrudeDirectionDefinition({
    enabled: legacyBackwardDepth > MIN_EXTRUDE_VALUE,
    depth: legacyBackwardDepth > MIN_EXTRUDE_VALUE ? legacyBackwardDepth : DEFAULT_EXTRUDE_LENGTH,
    reverse: false,
  });
}

/**
 * 归一化方向定义。
 *
 * @param input 输入方向定义。
 * @param fallback 回退定义。
 * @returns 归一化后的方向定义。
 */
function normalizeDirectionDefinition(
  input: ExtrudeDirectionDefinition | undefined,
  fallback: ExtrudeDirectionDefinition,
): NormalizedExtrudeDirectionDefinition {
  const source = input ?? fallback;
  const normalizedDraft: NormalizedExtrudeDraftDefinition = {
    enabled: resolveBoolean(source.draft?.enabled, fallback.draft.enabled),
    angleDeg: Math.min(90, resolvePositiveNumber(source.draft?.angleDeg, fallback.draft.angleDeg)),
    outward: resolveBoolean(source.draft?.outward, fallback.draft.outward),
    angleRad: 0,
  };
  normalizedDraft.angleRad = normalizedDraft.angleDeg * (Math.PI / 180);

  return {
    enabled: resolveBoolean(source.enabled, fallback.enabled),
    endCondition: source.endCondition,
    depth: resolvePositiveNumber(source.depth, fallback.depth),
    reverse: resolveBoolean(source.reverse, fallback.reverse),
    draft: normalizedDraft,
    target: cloneExtrudeEndTarget(source.target),
    offset: typeof source.offset === 'number' && Number.isFinite(source.offset) ? Math.max(0, source.offset) : DEFAULT_EXTRUDE_LENGTH,
    reverseOffset: source.reverseOffset === true,
    translateSurface: source.translateSurface === true,
  };
}

/**
 * 归一化薄壁特征定义。
 *
 * @param input 输入薄壁定义。
 * @returns 归一化后的薄壁定义。
 */
function normalizeThinFeatureDefinition(
  input: ExtrudeThinFeatureDefinition | undefined,
): NormalizedExtrudeThinFeatureDefinition {
  const fallback = createDefaultExtrudeThinFeatureDefinition();
  const source = input ?? fallback;

  return {
    enabled: resolveBoolean(source.enabled, fallback.enabled),
    type: source.type ?? fallback.type,
    reverse: resolveBoolean(source.reverse, fallback.reverse),
    thickness: resolvePositiveNumber(source.thickness, fallback.thickness),
    thickness1: resolvePositiveNumber(source.thickness1, fallback.thickness1),
    thickness2: resolvePositiveNumber(source.thickness2, fallback.thickness2),
    capEnds: resolveBoolean(source.capEnds, fallback.capEnds),
    capThickness: resolvePositiveNumber(source.capThickness, fallback.capThickness),
  };
}

/**
 * 计算 legacy 执行链需要的 flat 深度映射。
 *
 * @param direction1 方向 1。
 * @param direction2 方向 2。
 * @returns legacy 映射结果。
 */
function buildLegacyDistancePayload(input: {
  direction1: NormalizedExtrudeDirectionDefinition;
  direction2: NormalizedExtrudeDirectionDefinition;
}): {
  depth: number;
  direction: 'normal' | 'reverse';
  startOffset: number;
} {
  if (input.direction1.endCondition === 'mid_plane') {
    return { depth: input.direction1.depth, direction: 'normal', startOffset: -input.direction1.depth / 2 };
  }
  const direction1Enabled = input.direction1.enabled && input.direction1.depth > MIN_EXTRUDE_VALUE;
  const direction2Enabled = input.direction2.enabled && input.direction2.depth > MIN_EXTRUDE_VALUE;

  if (direction2Enabled) {
    return {
      depth: input.direction1.depth + input.direction2.depth,
      direction: input.direction1.reverse ? 'reverse' : 'normal',
      startOffset: input.direction1.reverse ? input.direction2.depth : -input.direction2.depth,
    };
  }

  return {
    depth: input.direction1.depth,
    direction: input.direction1.reverse ? 'reverse' : 'normal',
    startOffset: 0,
  };
}

/**
 * 归一化拉伸特征定义。
 *
 * @param params 原始拉伸参数。
 * @returns 统一后的拉伸特征定义。
 */
export function normalizeExtrudeFeatureDefinition(
  params: ExtrudeParams,
): NormalizedExtrudeFeatureDefinition {
  const fallbackDirection1 = resolveDirection1FromLegacyParams(params);
  const fallbackDirection2 = resolveDirection2FromLegacyParams(params);
  const direction1 = normalizeDirectionDefinition(params.direction1, fallbackDirection1);
  const direction2 = normalizeDirectionDefinition(params.direction2, fallbackDirection2);
  if (params.operation === 'cut' || params.featureKind === 'cut-extrude') {
    // 切除本轮仍只开放已实现的给定深度，不能借用凸台界面暴露占位条件。
    direction1.endCondition = 'blind';
    direction2.endCondition = 'blind';
  } else {
    const supported = ['blind', 'through_all', 'up_to_vertex', 'up_to_surface', 'offset_from_surface', 'up_to_body', 'mid_plane'];
    if (!supported.includes(direction1.endCondition) || !supported.includes(direction2.endCondition)) {
      throw new Error('拉伸凸台包含不支持的终止条件。');
    }
    if (direction1.endCondition === 'mid_plane') direction2.enabled = false;
    if (direction2.enabled && direction2.endCondition === 'mid_plane') {
      throw new Error('两侧对称只能用于方向 1。');
    }
  }
  const thinFeature = normalizeThinFeatureDefinition(params.thinFeature);
  const legacyPayload = buildLegacyDistancePayload({ direction1, direction2 });

  return {
    direction1,
    direction2,
    thinFeature,
    legacyDepth: legacyPayload.depth,
    legacyDirection: legacyPayload.direction,
    legacyStartOffset: legacyPayload.startOffset,
  };
}
